import { Splide, EventInterface } from "@splidejs/splide";

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

/**
 * Splide only applies `tabindex="-1"` to the nodes it is told about. Without this
 * list, the links in off-screen slides — and in the clones a looping carousel
 * builds — stay in the tab order while their slide carries `aria-hidden="true"`,
 * so keyboard users tab into content they cannot see.
 */
const FOCUSABLE_NODES =
    'a, button, input, textarea, select, details, iframe, [tabindex]:not([tabindex="-1"])';

/**
 * Overrides Splide's "Start autoplay"/"Pause autoplay", which don't say what the
 * button acts on. Splide swaps between the two as the state changes.
 */
const TOGGLE_LABELS = { play: "Play banner", pause: "Pause banner" };

export function DebrandTransition(Splide, Components) {
    const { bind } = EventInterface(Splide);
    const { Move } = Components;
    const { list } = Components.Elements;

    let endCallback;

    function mount() {
        bind(list, "transitionend", (e) => {
            if (e.target === list && endCallback) {
                // Removes the transition property
                cancel();

                // Calls the `done` callback
                endCallback();
            }
        });
    }

    function start(index, done) {
        // With no transition there is no `transitionend` to wait on, so the move
        // has to be reported complete here or the carousel never settles. This
        // mirrors what Splide's own transition does at zero speed.
        if (reducedMotion.matches) {
            Move.jump(index);
            done();
            return;
        }

        // Converts the index to the position
        const destination = Move.toPosition(index, true);

        // Applies the CSS transition
        list.style.transition =
            "transform 1s cubic-bezier(0.645, 0.045, 0.355, 1)";

        // Moves the carousel to the destination.
        Move.translate(destination);

        // Keeps the callback to invoke later.
        endCallback = done;
    }

    function cancel() {
        list.style.transition = "";
    }

    return {
        mount,
        start,
        cancel,
    };
}

/**
 * Splide gives every slide role="tabpanel" whenever pagination is enabled, and
 * clones inherit it because cloneDeep() copies the whole node and rewrites only the
 * class and id. Neither holds up: the carousels that hide their pagination in CSS
 * keep the tablist out of the accessibility tree, so no tab controls anything, and
 * a clone is never referenced by a tab even where one exists.
 *
 * In the ARIA carousel pattern slides are groups, not tab content — which is what
 * Splide itself emits when pagination is off — so normalise them to that. Setting
 * role="group" rather than deleting the attribute keeps aria-roledescription and
 * the "slide 3 of 7" label meaningful; both would be inert on a roleless div.
 *
 * The pagination is deliberately left alone: Splide drives the dots with a roving
 * tabindex and re-applies aria-selected on every move, so demoting them from tabs
 * would leave all but the active dot unreachable by keyboard.
 */
function normalizeSlideRoles(splide) {
    splide.root.querySelectorAll(".splide__slide").forEach(function (slide) {
        if (slide.classList.contains("splide__slide--clone")) {
            // A clone duplicates a real slide, so it carries no semantics itself.
            slide.removeAttribute("role");
            slide.removeAttribute("aria-roledescription");
            slide.removeAttribute("aria-label");
        } else if (slide.getAttribute("role") === "tabpanel") {
            slide.setAttribute("role", "group");
        }
    });
}

/**
 * Slides and clones only carry their attributes once mounted, and Splide rebuilds
 * both on refresh. Handlers fire in registration order, so binding after mount()
 * puts this behind Splide's own — normalising the rebuilt nodes, not the discarded
 * ones.
 */
function fixSlideRoles(splide) {
    normalizeSlideRoles(splide);
    splide.on("refresh", function () {
        normalizeSlideRoles(splide);
    });
}

/**
 * The background videos are started here rather than through an `autoplay`
 * attribute, so that playback can be withheld from a visitor who has asked for
 * reduced motion — an attribute would have started before any script could
 * intervene.
 *
 * The banner's play/pause button is deliberately not wired to these: it stops the
 * slide rotation and leaves the video on the slide you are looking at running.
 */
function startBannerVideos(splide) {
    if (reducedMotion.matches) {
        return;
    }

    // Clones included: a clone's video is on screen during the transition.
    splide.root.querySelectorAll("video").forEach(function (video) {
        // A rejected play() — an aborted load, a stricter autoplay policy — must
        // not take the rest of the banner down with it.
        const played = video.play();
        if (played && played.catch) {
            played.catch(function () {});
        }
    });
}

document.addEventListener("DOMContentLoaded", function () {
    let homepageSlider = document.querySelectorAll(".homepageBanner .splide");
    homepageSlider.forEach(function (element) {
        let interval = element.getAttribute("data-interval");
        let slideCount = element.querySelectorAll(
            ".splide__list > .splide__slide"
        ).length;

        // A lone slide can't rotate — Splide's `isEnough()` refuses to start
        // autoplay for one — so enabling autoplay for one would only produce a
        // toggle button with nothing to toggle. The template withholds the button
        // on the same condition.
        let autoScrolls = parseInt(interval) > 0 && slideCount > 1;

        // `'pause'` mounts autoplay without starting it: the interval, the button
        // and the live region are all wired up, but nothing moves until asked.
        let autoplay = autoScrolls
            ? reducedMotion.matches
                ? "pause"
                : true
            : false;

        var splide = new Splide(element, {
            type: "loop",
            mediaQuery: "min",
            perPage: 1,
            arrows: false,
            pagination: true,
            autoplay: autoplay,
            interval: interval ? interval : 6000,
            focusableNodes: FOCUSABLE_NODES,
            // `aria-roledescription="carousel"` is only meaningful on an element
            // that has an accessible name, and Splide sets the roledescription
            // unconditionally.
            label: "Featured promotions",
            i18n: TOGGLE_LABELS,
            breakpoints: {
                600: {},
                1200: {},
                1800: {},
                2400: {},
                3000: {},
            },
        });

        splide.on("move", function (newIndex, oldIndex, destIndex) {
            // Restart all video slides that are not the current slide
            var slides = splide.Components.Elements.slides;
            slides.forEach(function (slide, index) {
                var video = slide.querySelector("video");
                if (video) {
                    if (index === newIndex) {
                        video.currentTime = 0;
                    }
                }
            });
        });

        splide.mount({}, DebrandTransition);
        fixSlideRoles(splide);
        startBannerVideos(splide);
    });

    let gallerySlider = document.querySelectorAll(
        ".block.gallery.slider .splide"
    );
    gallerySlider.forEach(function (element) {
        var slider = new Splide(element, {
            type: "slide",
            mediaQuery: "min",
            focusableNodes: FOCUSABLE_NODES,
            gap: 32,
            start: 1,
            perPage: 1,
            padding: { left: 0, right: 48 },
            breakpoints: {
                600: {
                    perPage: 2,
                    padding: { left: 0, right: 64 },
                },
                1200: {
                    perPage: 3,
                    padding: { left: 0, right: 96 },
                },
                1800: {
                    perPage: 4,
                    padding: { left: 0, right: 128 },
                },
                2400: {
                    perPage: 5,
                },
                3000: {
                    perPage: 6,
                },
            },
            arrowPath:
                "M29.375,19.375 L12.1328127,19.375 L20.4375005,11.0703135 C20.6816411,10.8261728 20.6816411,10.4306245 20.4375005,10.1867199 C20.1933599,9.94281292 19.7978127,9.94257927 19.5539069,10.1867199 L10.1789069,19.5617187 C9.93476629,19.8058593 9.93476629,20.2014065 10.1789069,20.4453135 L19.5539069,29.8203135 C19.679687,29.937501 19.835937,30 20,30 C20.164063,30 20.3198433,29.9389601 20.4418755,29.8168755 C20.6860161,29.5727348 20.6860161,29.1771889 20.4418755,28.9332819 L12.1328127,20.625 L29.375,20.625 C29.7187495,20.625 30,20.3437495 30,20 C30,19.6562505 29.7187495,19.375 29.375,19.375 Z M20,0 C8.95312548,0 0,8.95312548 0,20 C0,31.0468745 8.95312548,40 20,40 C31.0468745,40 40,31.0468745 40,20 C40,8.95312548 31.0468745,0 20,0 Z M20,38.75 C9.66406226,38.75 1.25,30.3359365 1.25,20 C1.25,9.66406345 9.66406226,1.25 20,1.25 C30.3359365,1.25 38.75,9.66406345 38.75,20 C38.75,30.3359365 30.3359365,38.75 20,38.75 Z",
        });
        slider.mount();
        fixSlideRoles(slider);
    });

    let giftCardSlider = document.querySelectorAll(".step .gift-cards .splide");
    giftCardSlider.forEach(function (element) {
        var slider = new Splide(element, {
            type: "slide",
            // Pagination is display:none in CSS, so its tablist never reaches
            // the accessibility tree. Off means Splide emits role="group" on
            // slides natively and builds no dead pagination DOM.
            pagination: false,
            mediaQuery: "min",
            focusableNodes: FOCUSABLE_NODES,
            gap: 64,
            start: 1,
            perPage: 1,
            breakpoints: {
                768: {
                    perPage: 2,
                    padding: 64,
                },
                1200: {
                    perPage: 3,
                },
            },
            arrowPath:
                "M29.375,19.375 L12.1328127,19.375 L20.4375005,11.0703135 C20.6816411,10.8261728 20.6816411,10.4306245 20.4375005,10.1867199 C20.1933599,9.94281292 19.7978127,9.94257927 19.5539069,10.1867199 L10.1789069,19.5617187 C9.93476629,19.8058593 9.93476629,20.2014065 10.1789069,20.4453135 L19.5539069,29.8203135 C19.679687,29.937501 19.835937,30 20,30 C20.164063,30 20.3198433,29.9389601 20.4418755,29.8168755 C20.6860161,29.5727348 20.6860161,29.1771889 20.4418755,28.9332819 L12.1328127,20.625 L29.375,20.625 C29.7187495,20.625 30,20.3437495 30,20 C30,19.6562505 29.7187495,19.375 29.375,19.375 Z M20,0 C8.95312548,0 0,8.95312548 0,20 C0,31.0468745 8.95312548,40 20,40 C31.0468745,40 40,31.0468745 40,20 C40,8.95312548 31.0468745,0 20,0 Z M20,38.75 C9.66406226,38.75 1.25,30.3359365 1.25,20 C1.25,9.66406345 9.66406226,1.25 20,1.25 C30.3359365,1.25 38.75,9.66406345 38.75,20 C38.75,30.3359365 30.3359365,38.75 20,38.75 Z",
        });
        slider.mount();
        fixSlideRoles(slider);
    });

    let featuredProductSlider = document.querySelectorAll(
        ".block.featuredProducts .splide"
    );
    featuredProductSlider.forEach(function (element) {
        var slider = new Splide(element, {
            type: "slide",
            // Pagination is display:none in CSS, so its tablist never reaches
            // the accessibility tree. Off means Splide emits role="group" on
            // slides natively and builds no dead pagination DOM.
            pagination: false,
            mediaQuery: "min",
            focusableNodes: FOCUSABLE_NODES,
            gap: 18,
            start: 0,
            perPage: 1,
            padding: 16,
            breakpoints: {
                640: {
                    perPage: 2,
                    padding: 64,
                },
                768: {
                    perPage: 3,
                },
                1200: {
                    perPage: 4,
                    padding: 128,
                },
            },
            arrowPath:
                "M29.375,19.375 L12.1328127,19.375 L20.4375005,11.0703135 C20.6816411,10.8261728 20.6816411,10.4306245 20.4375005,10.1867199 C20.1933599,9.94281292 19.7978127,9.94257927 19.5539069,10.1867199 L10.1789069,19.5617187 C9.93476629,19.8058593 9.93476629,20.2014065 10.1789069,20.4453135 L19.5539069,29.8203135 C19.679687,29.937501 19.835937,30 20,30 C20.164063,30 20.3198433,29.9389601 20.4418755,29.8168755 C20.6860161,29.5727348 20.6860161,29.1771889 20.4418755,28.9332819 L12.1328127,20.625 L29.375,20.625 C29.7187495,20.625 30,20.3437495 30,20 C30,19.6562505 29.7187495,19.375 29.375,19.375 Z M20,0 C8.95312548,0 0,8.95312548 0,20 C0,31.0468745 8.95312548,40 20,40 C31.0468745,40 40,31.0468745 40,20 C40,8.95312548 31.0468745,0 20,0 Z M20,38.75 C9.66406226,38.75 1.25,30.3359365 1.25,20 C1.25,9.66406345 9.66406226,1.25 20,1.25 C30.3359365,1.25 38.75,9.66406345 38.75,20 C38.75,30.3359365 30.3359365,38.75 20,38.75 Z",
        });
        slider.mount();
        fixSlideRoles(slider);
    });

    let productCollectionsSlider = document.querySelectorAll(
        ".block.productCollections .splide"
    );
    productCollectionsSlider.forEach(function (element) {
        var slider = new Splide(element, {
            type: "slide",
            // Pagination is display:none in CSS, so its tablist never reaches
            // the accessibility tree. Off means Splide emits role="group" on
            // slides natively and builds no dead pagination DOM.
            pagination: false,
            mediaQuery: "min",
            focusableNodes: FOCUSABLE_NODES,
            gap: 18,
            start: 1,
            perPage: 1,
            padding: 16,
            breakpoints: {
                640: {
                    perPage: 2,
                    padding: 64,
                },
                768: {
                    perPage: 3,
                },
                1200: {
                    perPage: 4,
                    padding: 128,
                },
            },
            arrowPath:
                "M29.375,19.375 L12.1328127,19.375 L20.4375005,11.0703135 C20.6816411,10.8261728 20.6816411,10.4306245 20.4375005,10.1867199 C20.1933599,9.94281292 19.7978127,9.94257927 19.5539069,10.1867199 L10.1789069,19.5617187 C9.93476629,19.8058593 9.93476629,20.2014065 10.1789069,20.4453135 L19.5539069,29.8203135 C19.679687,29.937501 19.835937,30 20,30 C20.164063,30 20.3198433,29.9389601 20.4418755,29.8168755 C20.6860161,29.5727348 20.6860161,29.1771889 20.4418755,28.9332819 L12.1328127,20.625 L29.375,20.625 C29.7187495,20.625 30,20.3437495 30,20 C30,19.6562505 29.7187495,19.375 29.375,19.375 Z M20,0 C8.95312548,0 0,8.95312548 0,20 C0,31.0468745 8.95312548,40 20,40 C31.0468745,40 40,31.0468745 40,20 C40,8.95312548 31.0468745,0 20,0 Z M20,38.75 C9.66406226,38.75 1.25,30.3359365 1.25,20 C1.25,9.66406345 9.66406226,1.25 20,1.25 C30.3359365,1.25 38.75,9.66406345 38.75,20 C38.75,30.3359365 30.3359365,38.75 20,38.75 Z",
        });
        slider.mount();
        fixSlideRoles(slider);
    });
});
